import React, { useCallback, useContext, useState } from 'react';
import { ContextProps } from '../types/ContextProps';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { OfflineLevelEnum } from '../enums/offlineLevelEnum';
import { getSettingsItem, putSettingsItem } from 'src/api/offline/offlineSettingsApi';
import type { ConnectionSyncState } from 'src/components/page-header/connection-status/connectionStatusModel';

export type ConnectionRecoveryAction = () => Promise<void>;
export type RecoveryDomain = 'events' | 'stowage' | 'aolBunker';
export type RecoveryDomainState = 'pending' | 'current' | 'failed' | 'blocked-contract';

export interface ReconnectNotice {
    title: string;
    message: string;
}

export interface RecoveryDomainStatus {
    state: RecoveryDomainState;
    recoveredAt: Date | null;
}

export type RecoveryDomainStatuses = Record<RecoveryDomain, RecoveryDomainStatus>;

const createPendingRecoveryDomains = (): RecoveryDomainStatuses => ({
    events: { state: 'pending', recoveredAt: null },
    stowage: { state: 'pending', recoveredAt: null },
    aolBunker: { state: 'pending', recoveredAt: null },
});

interface ConnectionContextType {
    connectionStatus: HubConnectionState;
    setConnectionStatus: (hubConnectionState: HubConnectionState) => void;
    hubConnection: HubConnection | null;
    setHubConnection: (hub: HubConnection | null) => void;
    connectedVesselGroup: number | null;
    setConnectedVesselGroup: (imoNumber: number | null) => void;
    syncState: ConnectionSyncState;
    setSyncState: (state: ConnectionSyncState) => void;
    lastTransportConnectedAt: Date | null;
    lastSignalRActivityAt: Date | null;
    setLastSignalRActivityAt: (timestamp: Date | null) => void;
    recoveryDomains: RecoveryDomainStatuses;
    setRecoveryDomainState: (domain: RecoveryDomain, state: RecoveryDomainState) => void;
    resetSignalRSessionState: () => void;
    requestSignalRRecovery: ConnectionRecoveryAction | null;
    setSignalRRecoveryAction: (action: ConnectionRecoveryAction | null) => void;
    reconnectNotice: ReconnectNotice | null;
    setReconnectNotice: (notice: ReconnectNotice | null) => void;
    clearReconnectNotice: () => void;
    offlineLevel: OfflineLevelEnum;
    isOffline: boolean;
    setOfflineLevel: (status: OfflineLevelEnum) => void;
    isOfflineSimulated: () => Promise<boolean>;
    setIsOfflineSimulated: (value: boolean) => Promise<void>;
}
export const ConnectionContext = React.createContext<ConnectionContextType>(undefined!);

export const ConnectionProvider = ({ children }: ContextProps): React.ReactElement => {
    const [connectionStatus, setConnectionStatus] = useState<HubConnectionState>(HubConnectionState.Disconnected);
    const [hubConnection, setHubConnection] = useState<HubConnection | null>(null);
    const [connectedVesselGroup, setConnectedVesselGroup] = useState<number | null>(null);
    const [syncState, setSyncState] = useState<ConnectionSyncState>('pending');
    const [lastTransportConnectedAt, setLastTransportConnectedAt] = useState<Date | null>(null);
    const [lastSignalRActivityAt, setLastSignalRActivityAt] = useState<Date | null>(null);
    const [recoveryDomains, setRecoveryDomains] = useState<RecoveryDomainStatuses>(createPendingRecoveryDomains);
    const [requestSignalRRecovery, setRequestSignalRRecovery] = useState<ConnectionRecoveryAction | null>(null);
    const [reconnectNotice, setReconnectNotice] = useState<ReconnectNotice | null>(null);
    const [offlineLevel, setOfflineLevel] = useState<OfflineLevelEnum>(OfflineLevelEnum.CheckingConnection);

    const clearReconnectNotice = useCallback((): void => {
        setReconnectNotice(null);
    }, []);

    const updateConnectionStatus = (hubConnectionState: HubConnectionState): void => {
        setConnectionStatus(hubConnectionState);
        if (hubConnectionState === HubConnectionState.Connected) {
            setLastTransportConnectedAt(new Date());
        }
    };

    const updateConnectedVesselGroup = (imoNumber: number | null): void => {
        setConnectedVesselGroup(imoNumber);
    };

    const setSignalRRecoveryAction = useCallback((action: ConnectionRecoveryAction | null): void => {
        setRequestSignalRRecovery(() => action);
    }, []);

    const resetSignalRSessionState = useCallback((): void => {
        setConnectionStatus(HubConnectionState.Disconnected);
        setConnectedVesselGroup(null);
        setSyncState('pending');
        setLastTransportConnectedAt(null);
        setLastSignalRActivityAt(null);
        setRecoveryDomains(createPendingRecoveryDomains());
        setReconnectNotice(null);
    }, []);

    const setRecoveryDomainState = useCallback((domain: RecoveryDomain, state: RecoveryDomainState): void => {
        setRecoveryDomains((current) => ({
            ...current,
            [domain]: {
                state,
                recoveredAt: state === 'current' ? new Date() : null,
            },
        }));
    }, []);

    const isOfflineSimulated = async (): Promise<boolean> => {
        const isOfflineSimulated = await getSettingsItem('isOfflineSimulated');
        return isOfflineSimulated?.isOfflineSimulated ?? false;
    };

    const setIsOfflineSimulated = async (value: boolean): Promise<void> => {
        setOfflineLevel(value === true ? OfflineLevelEnum.Offline : OfflineLevelEnum.Online);
        await putSettingsItem({ isOfflineSimulated: value }, 'isOfflineSimulated');
    };

    const isOffline = offlineLevel !== OfflineLevelEnum.Online;

    return (
        <ConnectionContext.Provider
            value={{
                connectionStatus,
                setConnectionStatus: updateConnectionStatus,
                hubConnection,
                setHubConnection,
                connectedVesselGroup,
                setConnectedVesselGroup: updateConnectedVesselGroup,
                syncState,
                setSyncState,
                lastTransportConnectedAt,
                lastSignalRActivityAt,
                setLastSignalRActivityAt,
                recoveryDomains,
                setRecoveryDomainState,
                resetSignalRSessionState,
                requestSignalRRecovery,
                setSignalRRecoveryAction,
                reconnectNotice,
                setReconnectNotice,
                clearReconnectNotice,
                offlineLevel,
                setOfflineLevel,
                isOfflineSimulated,
                setIsOfflineSimulated,
                isOffline,
            }}
        >
            {children}
        </ConnectionContext.Provider>
    );
};

export const useConnectionStore = (): ConnectionContextType => useContext(ConnectionContext);
